import type { FlowStep } from "../types";
import { FlowStepError } from "../engine/errors";
import {
  orderItemsForSupplier,
  requireChild,
  shortId,
  supplierTransitionPath,
  todayForInvoice,
  verifyEq,
  waitForSnapshot,
} from "./common";

/** Existing S3 invoice PDF — skip /media upload (multipart is unreliable in the tester). */
const HARDCODED_INVOICE_MEDIA_ID = "699ffd099a036881e2f57810";

export const STEP_SUPPLIER_SESSION = "supplier_session";
export const STEP_SUPPLIER_ACK = "supplier_ack";
export const STEP_SUPPLIER_INVOICE_MEDIA = "supplier_invoice_media";
export const STEP_SUPPLIER_REVIEW = "supplier_review";
export const STEP_SUPPLIER_READY = "supplier_ready";

const buildSupplierInvoiceInfo = (rt: Parameters<FlowStep["execute"]>[0]) => {
  const invoice = rt.ctx.invoice;
  if (!invoice) throw new FlowStepError("Invoice media has not been uploaded yet.");
  const items = rt.ctx.snapshot?.child?.orderItems || [];
  const totalQty = items.reduce((sum, i) => sum + (i.orderedQuantity || 0), 0);
  return {
    invoiceNo: invoice.invoiceNo,
    invoiceAmount: invoice.amount,
    invoiceDate: todayForInvoice(),
    invoiceMediaId: invoice.mediaId,
    noOfItems: String(items.length || rt.ctx.config.parts.length),
    totalQtyOfItems: String(totalQty || rt.ctx.config.parts.reduce((s, p) => s + p.quantity, 0)),
  };
};

export const supplierSessionStep: FlowStep = {
  id: STEP_SUPPLIER_SESSION,
  name: "Authenticate as supplier",
  targetLabel: "Supplier session ready",
  description:
    "Obtains a supplier JWT via the same proxy login the distributor app uses (GET /otp/verifyProxyUser). The proxy secret is asked once and stored in localStorage; the JWT is kept in sessionStorage only.",
  actor: "SUPPLIER",
  kind: "api",
  estimateMs: [800, 3000],
  execute: async (rt) => {
    await rt.getSupplierToken();
    rt.log(`Supplier session active for ${rt.ctx.supplier.name}`, "success");
  },
};

export const supplierAckStep: FlowStep = {
  id: STEP_SUPPLIER_ACK,
  name: "Supplier acknowledges order",
  targetLabel: "Supplier ACK",
  description: "Supplier ACKs the allocated order (POST /v1/orders/:id/transition?action=ACK).",
  actor: "SUPPLIER",
  kind: "api",
  estimateMs: [1000, 4000],
  shouldSkip: (ctx) => {
    const st = ctx.snapshot?.child?.supplierStatus;
    return st && st !== "NEW" ? `Supplier allocation already ${st}` : null;
  },
  execute: async (rt) => {
    const child = requireChild(rt.ctx);
    const token = await rt.getSupplierToken();
    await rt.api({
      label: "Supplier ACK",
      method: "POST",
      path: supplierTransitionPath(child.orderId, "ACK"),
      body: {},
      actor: "SUPPLIER",
      authToken: token,
    });
  },
  verify: (s) =>
    verifyEq("allocatedTo.orderStatus", ["ACK", "PICKED", "IN_REVIEW", "READY"], s.child?.supplierStatus),
};

export const supplierInvoiceMediaStep: FlowStep = {
  id: STEP_SUPPLIER_INVOICE_MEDIA,
  name: "Attach supplier invoice",
  targetLabel: "Supplier invoice attached",
  description:
    "Reuses a known test invoice PDF on S3 (media 699ffd099a036881e2f57810). That media id is attached on the READY/REVIEW transition.",
  actor: "SUPPLIER",
  kind: "api",
  estimateMs: [200, 800],
  execute: async (rt) => {
    requireChild(rt.ctx);
    const invoiceNo = `${rt.ctx.config.invoice.invoiceNoPrefix || "TST"}-${shortId()}`.slice(0, 16);
    rt.ctx.invoice = {
      mediaId: HARDCODED_INVOICE_MEDIA_ID,
      invoiceNo,
      amount: rt.ctx.config.invoice.amount || "0",
    };
    rt.log(
      `Invoice ${invoiceNo} attached to hardcoded media ${HARDCODED_INVOICE_MEDIA_ID}`,
      "success"
    );
  },
};

export const supplierReviewStep: FlowStep = {
  id: STEP_SUPPLIER_REVIEW,
  name: "Supplier submits for review",
  targetLabel: "Supplier IN_REVIEW",
  description:
    "Lean-flow suppliers submit the invoice for Garaaz review (transition?action=REVIEW with supplierInvoiceInfo) → IN_REVIEW. Skipped for autonomous suppliers.",
  actor: "SUPPLIER",
  kind: "api",
  estimateMs: [1000, 5000],
  shouldSkip: (ctx) => {
    if (!ctx.supplier.isLeanFlow) return "Autonomous supplier: REVIEW is not allowed, READY is direct";
    const st = ctx.snapshot?.child?.supplierStatus;
    return st === "IN_REVIEW" || st === "READY" ? `Supplier allocation already ${st}` : null;
  },
  execute: async (rt) => {
    const child = requireChild(rt.ctx);
    const token = await rt.getSupplierToken();
    await rt.api({
      label: "Supplier REVIEW (with invoice)",
      method: "POST",
      path: supplierTransitionPath(child.orderId, "REVIEW"),
      body: {
        orderItems: orderItemsForSupplier(rt.ctx),
        supplierInvoiceInfo: buildSupplierInvoiceInfo(rt),
      },
      actor: "SUPPLIER",
      authToken: token,
    });
  },
  verify: (s) => verifyEq("allocatedTo.orderStatus", ["IN_REVIEW", "READY"], s.child?.supplierStatus),
};

export const supplierReadyStep: FlowStep = {
  id: STEP_SUPPLIER_READY,
  name: "Supplier marks READY",
  targetLabel: "Supplier READY",
  description:
    "Autonomous: transition?action=READY with supplierInvoiceInfo (emits INVOICE_UPLOADED). Lean: READY from IN_REVIEW after Garaaz review (emits REVIEWED_INVOICE_UPLOADED + SUPPLIER_READY).",
  actor: "SUPPLIER",
  kind: "api",
  estimateMs: [1500, 6000],
  shouldSkip: (ctx) =>
    ctx.snapshot?.child?.supplierStatus === "READY" ? "Supplier allocation already READY" : null,
  execute: async (rt) => {
    const child = requireChild(rt.ctx);
    const token = await rt.getSupplierToken();
    const isLean = rt.ctx.supplier.isLeanFlow;
    const body = isLean
      ? { orderItems: orderItemsForSupplier(rt.ctx) }
      : { orderItems: orderItemsForSupplier(rt.ctx), supplierInvoiceInfo: buildSupplierInvoiceInfo(rt) };

    await rt.api({
      label: isLean ? "Supplier READY (after review)" : "Supplier READY (with invoice)",
      method: "POST",
      path: supplierTransitionPath(child.orderId, "READY"),
      body,
      actor: "SUPPLIER",
      authToken: token,
    });
    rt.markEventProcessed();

    await waitForSnapshot(rt, {
      trigger: "Supplier READY transition",
      expected: "allocatedTo.orderStatus = READY",
      check: (s) => s.child?.supplierStatus === "READY",
      describe: (s) => `supplier=${s.child?.supplierStatus}, order=${s.child?.orderStatus}`,
      maxAttempts: 3,
    });
  },
  verify: (s) => verifyEq("allocatedTo.orderStatus", "READY", s.child?.supplierStatus),
};
